// src/pages/admin/AdminPageEditor.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { sectionTemplates } from "../../data/database";
import { slugify, isReservedSlug } from "../../data/pagesStore";
import { pagesApi } from "../../api/pagesApi";

import SectionSelector from "../componenets/SectionSelector";

import SectionEditorModal from "./components/SectionEditorModal";
import FooterEditorModal from "./components/FooterEditorModal";
import NavEditorModal from "./components/NavEditorModal";

import TabsBar from "./editorTabs/TabsBar";
import ConfirmDeleteModal from "../../components/GeneralComponents/ConfirmDeleteModal";
import Toast from "../../components/GeneralComponents/Toast";
import { normalizeLimits, validatePageAgainstLimitsClient } from "./utils/limitsUtils";

import EditorHeader from "./adminPageEditor/components/EditorHeader";
import NonEditorTabsPanel from "./adminPageEditor/components/NonEditorTabsPanel";
import EditorTabPanel from "./adminPageEditor/components/EditorTabPanel";

import { useLanguageDict } from "./adminPageEditor/hooks/useLanguageDict";
import { useMobileEditorView } from "./adminPageEditor/hooks/useMobileEditorView";
import { useSectionAutosave } from "./adminPageEditor/hooks/useSectionAutosave";
import { useBlockAutosave } from "./adminPageEditor/hooks/useBlockAutosave";
import InfoDialog from "../../components/GeneralComponents/InfoDialog";
import {
  resolveSectionTypeKey,
  buildTemplatePatch,
  hydrateEditorState,
  deepClone,
  buildGeneralPayload,
} from "./adminPageEditor/utils/editorUtils";

export default function AdminPageEditor() {
  const navigate = useNavigate();
  const { pageId } = useParams();

  const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });
  const [tab, setTab] = useState("editor");
  const isEditorTab = tab === "editor";

  const { mobileEditorView, setMobileEditorView } = useMobileEditorView(tab);

  // page + main editable state
  const [page, setPage] = useState(null);
  const [pageName, setPageName] = useState("");
  const [pageSlug, setPageSlug] = useState("");
  const [general, setGeneral] = useState({
    phone: "",
    city: "",
    logoUrl: "",
    pageBgColor: "#ffffff",
    scrollAnim: "fadeUp",
    scrollOnce: true,
    scrollBehavior: "smooth",
    language: "en",
    direction: "ltr",
  });

  const [limits, setLimits] = useState({});
  const [nav, setNav] = useState(null);
  const [footer, setFooter] = useState(null);
  const [whatsapp, setWhatsapp] = useState(null);
  const [sections, setSections] = useState([]);

  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  const [saving, setSaving] = useState(false);

  // modals + section actions
  const [showTemplateSelector, setShowTemplateSelector] = useState(null);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [showSectionEditor, setShowSectionEditor] = useState(false);
  const [showFooterEditor, setShowFooterEditor] = useState(false);
  const [showNavEditor, setShowNavEditor] = useState(false);

  const [pendingRemoveIndex, setPendingRemoveIndex] = useState(null);
  const [isRemovingSection, setIsRemovingSection] = useState(false);

  const { t } = useLanguageDict({ apiBase: API_BASE, initialLang: "en", forceEnglish: true });

  // lock info 
  const [lockInfo, setLockInfo] = useState(null);
  const [takeoverDialog, setTakeoverDialog] = useState({
    open: false,
    type: "error",
    title: "Page is being edited",
    message: "",
    roleLabel: "",
  });
  const showToast = ({ type = "success", title = "", message = "" }) => {
    setToast({ open: true, type, title, message });
  };

  // autosave hooks
  const { patchSectionLive, flushSectionAutosave } = useSectionAutosave({
    pageId,
    updateSection: pagesApi.updateSection,
    setSections,
    sectionsRef,
  });

  const { patchBlockLive: patchNavLive, flushBlockAutosave: flushNavAutosave } = useBlockAutosave({
    pageId,
    updateBlock: pagesApi.updateNav,
    setBlock: setNav,
  });

  const { patchBlockLive: patchFooterLive, flushBlockAutosave: flushFooterAutosave } = useBlockAutosave({
    pageId,
    updateBlock: pagesApi.updateFooter,
    setBlock: setFooter,
  });

  // derived
  const editingSection = useMemo(
    () => sections.find((s) => s.id === editingSectionId) || null,
    [sections, editingSectionId]
  );

  // load page
  useEffect(() => {
    let ignore = false;

    async function load() {
      try {
        const found = await pagesApi.getById(pageId);
        if (ignore) return;

        setPage(found);

        const hydrated = hydrateEditorState(found, sectionTemplates, normalizeLimits);
        setPageName(hydrated.pageName);
        setPageSlug(hydrated.pageSlug);
        setGeneral(hydrated.general);
        setLimits(hydrated.limits);
        setWhatsapp(hydrated.whatsapp);
        setNav(hydrated.nav);
        setFooter(hydrated.footer);
        setSections(hydrated.sections);
      } catch (e) {
        console.error("Failed to load page:", e);
        if (!ignore) setPage(null);
      }
    }

    load();
    return () => {
      ignore = true;
    };
  }, [pageId]);

  // ✅ acquire edit lock
  useEffect(() => {
    if (!pageId) return;

    let stopped = false;
    let timer = null;

    async function acquire() {
      try {
        const r = await pagesApi.acquireEditLock(pageId);
        if (stopped) return;

        setLockInfo(r?.lock || null);

        const every = Number(r?.heartbeatEveryMs) || 25000;

        timer = setInterval(async () => {
          try {
            const hb = await pagesApi.heartbeatEditLock(pageId);
            if (stopped) return;

            if (hb?.expiresAt) {
              setLockInfo((prev) => (prev ? { ...prev, expiresAt: hb.expiresAt } : prev));
            }
          } catch (e) {
            // ✅ Redirect with dialog reason (no toast)
            navigate("/admin", {
              replace: true,
              state: {
                dialog: {
                  type: "error",
                  title: "Editing ended",
                  message: "You were removed because someone else started editing this page.",
                },
              },
            });
          }
        }, every);
      } catch (e) {
        const lock = e?.data?.lock;
        const roleRaw = String(lock?.role || "").toLowerCase();
        const roleLabel = roleRaw === "admin" ? "Admin" : roleRaw === "manager" ? "Manager" : "another user";
        const article = roleLabel === "Admin" ? "an" : "a";

        setTakeoverDialog({
          open: true,
          type: "error",
          title: "Page is being edited",
          message: `This page is currently being edited by ${article} ${roleLabel}. You can take over editing (the other editor will be removed).`,
          roleLabel,
        });
      }
    }

    acquire();

    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      pagesApi.releaseEditLock(pageId).catch(() => { });
    };
  }, [pageId, navigate]);

  // ---------- editor helpers ----------
  const updateSectionTemplate = (sectionIndex, templateKey) => {
    const section = sections[sectionIndex];
    if (!section) return;

    const patch = {
      ...buildTemplatePatch(section, templateKey, sectionTemplates),
      enabled: section.enabled ?? true,
      type: section.type,
    };

    patchSectionLive(section.id, patch);
    setShowTemplateSelector(null);
  };

  const onToggleSection = (index) => {
    const updated = [...sections];
    updated[index] = { ...updated[index], enabled: !updated[index].enabled };
    setSections(updated);
  };

  const onMoveSection = (index, direction) => {
    if ((direction === "up" && index === 0) || (direction === "down" && index === sections.length - 1)) return;
    const updated = [...sections];
    const newIndex = direction === "up" ? index - 1 : index + 1;
    const temp = updated[index];
    updated[index] = updated[newIndex];
    updated[newIndex] = temp;
    setSections(updated);
  };

  const onRemoveSection = (index) => {
    setPendingRemoveIndex(index);
  };

  const confirmRemoveSection = async () => {
    if (pendingRemoveIndex == null) return;

    setIsRemovingSection(true);
    try {
      const updated = [...sections];
      updated.splice(pendingRemoveIndex, 1);
      setSections(updated);
      setPendingRemoveIndex(null);
    } finally {
      setIsRemovingSection(false);
    }
  };

  const onEditSection = (section) => {
    setEditingSectionId(section.id);
    setShowSectionEditor(true);
  };
  const handleTakeover = async () => {
    try {
      await pagesApi.forceEditLock(pageId);

      // simplest + safest: reload editor page so it re-acquires heartbeat cleanly
      window.location.reload();
    } catch (e) {
      navigate("/admin", {
        replace: true,
        state: {
          dialog: {
            type: "error",
            title: "Takeover failed",
            message: e?.message || "Could not take over editing.",
          },
        },
      });
    }
  };
  const onAddSection = (type) => {
    const l = normalizeLimits(limits);
    if (typeof l.maxSections === "number" && sections.length >= l.maxSections) {
      alert("You reached the max sections limit for this page.");
      return;
    }

    const typeKey = resolveSectionTypeKey(type, sectionTemplates);
    const tpl = sectionTemplates?.[typeKey]?.template1;

    if (!tpl) {
      alert(`No template1 found for section type: "${typeKey}". Check sectionTemplates keys.`);
      return;
    }

    const tplStyle = deepClone(tpl?.style || {});
    if (!Object.prototype.hasOwnProperty.call(tplStyle, "bgColor")) {
      tplStyle.bgColor = "";
    }

    const newSection = {
      id: `section-${Date.now()}-${Math.random().toString(16).slice(2)}`,
      type: typeKey,
      template: "template1",
      enabled: true,
      config: deepClone(tpl?.config || {}),
      style: tplStyle,
    };

    setSections((prev) => [...prev, newSection]);
  };

  // ---------- actions ----------
  const saveAll = async () => {
    if (!page) return;

    const nextSlug = slugify(pageSlug || pageName);
    if (!nextSlug) {
      showToast({ type: "error", title: "Invalid slug", message: "Please enter a valid slug before saving." });
      return;
    }
    if (isReservedSlug(nextSlug)) {
      showToast({ type: "error", title: "Reserved slug", message: "This slug is reserved. Choose a different one." });
      return;
    }

    const limitsNormalized = normalizeLimits(limits);
    const validation = validatePageAgainstLimitsClient({ sections, nav, footer }, limitsNormalized);
    if (!validation.ok) {
      showToast({
        type: "error",
        title: "Validation failed",
        message: validation.errors[0] || "Please review your page limits.",
      });
      return;
    }

    setSaving(true);
    try {
      const updated = await pagesApi.update(pageId, {
        name: pageName.trim() || "Untitled",
        slug: nextSlug,
        general: buildGeneralPayload(general),
        limits: limitsNormalized,
        sections,
        footer,
        nav,
        whatsapp,
      });

      setPage(updated);

      const hydrated = hydrateEditorState(updated, sectionTemplates, normalizeLimits);
      setPageName(hydrated.pageName);
      setPageSlug(hydrated.pageSlug);
      setGeneral(hydrated.general);
      setLimits(hydrated.limits);
      setWhatsapp(hydrated.whatsapp);
      setNav(hydrated.nav);
      setFooter(hydrated.footer);
      setSections(hydrated.sections);

      showToast({ type: "success", title: "Saved", message: "Your page changes were saved successfully." });
    } catch (e) {
      showToast({ type: "error", title: "Save failed", message: e?.message || "Failed to save page changes." });
    } finally {
      setSaving(false);
    }
  };

  const preview = () => {
    const previewSlug = slugify(pageSlug || pageName);
    localStorage.setItem("smartbusiness-preview", JSON.stringify({ sections, footer, nav, whatsapp, general }));
    navigate("/admin/preview", {
      state: { config: { sections, footer, nav, whatsapp, general }, slugOverride: previewSlug },
    });
  };

  const openPage = () => {
    const nextSlug = slugify(pageSlug || pageName);
    window.open(`/${nextSlug}`, "_blank");
  };

  // ---------- UI ----------
  if (!page) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6 overflow-x-hidden">
        <div className="bg-white rounded-2xl shadow-sm border p-6 text-center w-full max-w-md">
          <div className="text-xl font-bold text-gray-900">Page not found</div>
          <div className="text-gray-600 mt-2">This page id does not exist.</div>
          <button
            onClick={() => navigate("/admin")}
            className="mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  console.log("ADMIN PAGE EDITOR URL pageId:", pageId);
  console.log("ADMIN PAGE OBJECT:", page);
  console.log("ADMIN PAGE OBJECT id:", page?.id);
  console.log("ADMIN PAGE OBJECT _id:", page?._id);
  console.log("PAGE ID SENT TO SECTION MODAL:", page?.id || page?._id || pageId);

  const previewSlug = slugify(pageSlug || pageName);
  const limitsNormalized = normalizeLimits(limits);

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden">
      <EditorHeader
        pageName={pageName}
        previewSlug={previewSlug}
        isEditorTab={isEditorTab}
        onBack={() => navigate("/admin")}
        onOpen={openPage}
        onPreview={preview}
        onSave={saveAll}
        saving={saving}
      />
      <InfoDialog
        open={takeoverDialog.open}
        type={takeoverDialog.type}
        title={takeoverDialog.title}
        message={takeoverDialog.message}
        primaryText="Close"
        onClose={() => {
          setTakeoverDialog((p) => ({ ...p, open: false }));
          navigate("/admin", { replace: true });
        }}
        secondaryText="Take over"
        onSecondary={handleTakeover}
      />
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="bg-white rounded-2xl shadow-sm border p-2">
          <TabsBar value={tab} onChange={setTab} />
        </div>

        {!isEditorTab && (
          <NonEditorTabsPanel
            tab={tab}
            pageId={pageId}
            onToast={showToast}
            t={t}
            pageName={pageName}
            setPageName={setPageName}
            pageSlug={pageSlug}
            setPageSlug={setPageSlug}
            general={general}
            setGeneral={setGeneral}
            limits={limits}
            setLimits={setLimits}
          />
        )}

        {isEditorTab && (
          <EditorTabPanel
            mobileEditorView={mobileEditorView}
            setMobileEditorView={setMobileEditorView}
            sections={sections}
            limitsNormalized={limitsNormalized}
            onToggleSection={onToggleSection}
            onMoveSection={onMoveSection}
            onEditSection={onEditSection}
            onOpenTemplateSelector={(idx) => setShowTemplateSelector(idx)}
            onRemoveSection={onRemoveSection}
            onAddSection={onAddSection}
            nav={nav}
            onOpenNavEditor={() => setShowNavEditor(true)}
            footer={footer}
            onOpenFooterEditor={() => setShowFooterEditor(true)}
            whatsapp={whatsapp}
            setWhatsapp={setWhatsapp}
            general={general}
            previewSlug={previewSlug}
          />
        )}
      </div>

      {/* modals */}
      {showTemplateSelector !== null && sections[showTemplateSelector] && (
        <SectionSelector
          sectionType={sections[showTemplateSelector].type}
          currentTemplate={sections[showTemplateSelector].template}
          onSelectTemplate={(template) => updateSectionTemplate(showTemplateSelector, template)}
          onClose={() => setShowTemplateSelector(null)}
        />
      )}

  {showFooterEditor && footer && (
    <FooterEditorModal
      footer={footer}
      general={general}
      pageId={page?.id || page?._id || pageId}
      onPatch={patchFooterLive}
      onClose={async () => {
        await flushFooterAutosave();
        setShowFooterEditor(false);
      }}
      uploadImage={pagesApi.uploadImage}
    />
  )}

      {showSectionEditor && editingSection && (
        <SectionEditorModal
          section={editingSection}
          pageId={page?.id || page?._id}
          onPatch={(patch) => patchSectionLive(editingSection.id, patch)}
          onSave={async () => {
            await flushSectionAutosave(editingSection.id);
            setShowSectionEditor(false);
            setEditingSectionId(null);
          }}
          onClose={async () => {
            await flushSectionAutosave(editingSection.id);
            setShowSectionEditor(false);
            setEditingSectionId(null);
          }}
        />
      )}

    {showNavEditor && nav && (
      <NavEditorModal
        nav={nav}
        general={general}
        onPatch={patchNavLive}
        onClose={async () => {
          await flushNavAutosave();
          setShowNavEditor(false);
        }}
        uploadImage={pagesApi.uploadImage}
      />
    )}

      <ConfirmDeleteModal
        open={pendingRemoveIndex !== null}
        t={t}
        title={t("adminPageEditor.removeSectionTitle", "Remove section?")}
        message={t(
          "adminPageEditor.removeSectionMessage",
          "Are you sure you want to remove this section? This action cannot be undone."
        )}
        confirmText={t("adminPageEditor.removeSectionConfirm", "Remove")}
        cancelText={t("adminPageEditor.removeSectionCancel", "Cancel")}
        loading={isRemovingSection}
        onClose={() => {
          if (isRemovingSection) return;
          setPendingRemoveIndex(null);
        }}
        onConfirm={confirmRemoveSection}
      />

      <Toast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={() =>
          setToast((prev) => ({
            ...prev,
            open: false,
          }))
        }
      />
    </div>
  );
}